/* eslint-disable @typescript-eslint/consistent-type-definitions */
/* eslint-disable prettier/prettier */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from '../../../api';

export type IGroupUser = {
  userId: string;
  name: string;
  category: string;
  headshot: string;
  lastLogin: number;
  onboardStatus: string;
  onboardedDate:number;
  sentMessages: number;
  role: string[];
  goalProgress?: number | undefined;
  xpPoints: number;
  isActive: boolean;
  firstName: string;
  lastName: string;
  email: string;
  company: {
    companyName: string;
    companyLocation: string;
  };
  meetings: number;
  defaultMatch?: string[];
};

export interface IGroupInvitedUser {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  role: string[];
  userId: string;
  invitationId: string;

  // Campos opcionais
  mobileNumber?: string;
  phonePrefix?: string;
  orgLocation?: string;
  invitedAt?: string; // <── API retorna isto
  groups?: Array<{
    grpId: string;
    role: string[];
    enrolledCount: number;
    logo?: string;
    name?: string;
  }>; // <── API retorna isto
}


type IGroupUsers = {
  users: IGroupUser[];
  invitedUsers: IGroupInvitedUser[];
  showGoalProgress: any | undefined;
  pagination?: {
    pageNumber: number;
    pageSize: number;
    total: number;
  };
};

type IInitialState = {
  data: IGroupUsers | undefined;
  loading: boolean;
  error: boolean;
  errorText: string;
};

const initialState: IInitialState = {
  data: undefined,
  loading: false,
  error: false,
  errorText: ''
};

export const fetchGroupUsers = createAsyncThunk(
  'groupUsers',
  (args: string | { grpId: string; pageNumber?: number; pageSize?: number; name?: string }) => {
    if (typeof args === 'string') {
      return API.getGroupUsers(args).then((response) => response.data);
    }
    const { grpId, pageNumber, pageSize, name } = args;
    return API.getGroupUsers(grpId, pageNumber, pageSize, name).then((response) => response.data);
  }
);

const groupUsersSlice = createSlice({
  name: 'group users',
  initialState,
  reducers: {
    updateGroupUsers: (state, action) => {
      const users = state.data?.users || [];
      const invitedUsers = state.data?.invitedUsers || [];
      const showGoalProgress = state.data?.showGoalProgress;
      return {
        ...state,
        data: { users: [...users, action.payload], invitedUsers, showGoalProgress }
      };
    },
    updateUser: (state, action) => {
      const users =
        state.data?.users.map((each) => {
          if (each.userId === action.payload.userId) {
            return action.payload;
          }
          return each;
        }) || [];
      const invitedUsers = state.data?.invitedUsers || [];
      const showGoalProgress = state.data?.showGoalProgress;
      return {
        ...state,
        data: { users, invitedUsers, showGoalProgress }
      };
    },
    updateGroupInvitedUsers: (state, action) => {
      const users = state.data?.users || [];
      const invitedUsers = state.data?.invitedUsers || [];
      const showGoalProgress = state.data?.showGoalProgress;
      return {
        ...state,
        data: { users, invitedUsers: [...invitedUsers, action.payload], showGoalProgress }
      };
    },
    updateInvitedUser: (state, action) => {
      const users = state.data?.users || [];
      const showGoalProgress = state.data?.showGoalProgress;
      const invitedUsers =
        state.data?.invitedUsers.map((each) => {
          if (each.userId === action.payload.userId) {
            return action.payload;
          }
          return each;
        }) || [];
      return { ...state, data: { users, invitedUsers, showGoalProgress } };
    },
    removeUser: (state, action) => {
      const users = state.data?.users.filter((each) => each.userId !== action.payload.userId) || [];
      const invitedUsers = state.data?.invitedUsers || [];
      const showGoalProgress = state.data?.showGoalProgress;
      return { ...state, data: { users, invitedUsers, showGoalProgress } };
    },
    removeInvitedUser: (state, action) => {
      const users = state.data?.users || [];
      const showGoalProgress = state.data?.showGoalProgress;
      const invitedUsers =
        state.data?.invitedUsers.filter((each) => each.invitationId !== action.payload.invitationId) || [];
      return { ...state, data: { users, invitedUsers, showGoalProgress } };
    },
    updateGroupUserList: (state, action) => {
      // const users = state.data?.users || [];
      const invitedUsers = state.data?.invitedUsers || [];
      const showGoalProgress = state.data?.showGoalProgress;
      const pagination = state.data?.pagination;

      return {
        ...state,
        data: { users: action.payload, invitedUsers, showGoalProgress, pagination }
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupUsers.pending, (state, action) => {
        state.loading = true;
        state.error = false;
        const args = action.meta.arg;
        const pageNumber = typeof args === 'object' ? args.pageNumber : undefined;
        if (!pageNumber || pageNumber <= 1) {
          // Preserve invitedUsers so the client-side search on the Invited tab stays intact
          // while active-user results are being re-fetched from the server.
          state.data = state.data
            ? { ...state.data, users: [], pagination: undefined }
            : undefined;
        }
        state.errorText = '';
      })
      .addCase(fetchGroupUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = false;
        const args = action.meta.arg;
        const pageNumber = typeof args === 'object' ? args.pageNumber : undefined;
        const existingUsers = state.data?.users || [];
        const payloadRoot = action.payload?.data ?? action.payload ?? {};
        const payloadUsers: IGroupUser[] = payloadRoot?.users || [];
        let nextUsers: typeof existingUsers;
        if (pageNumber && pageNumber > 1) {
          const seen = new Map<string, (typeof existingUsers)[0]>();
          existingUsers.forEach((u) => seen.set(u.userId, u));
          payloadUsers.forEach((u) => seen.set(u.userId, u));
          nextUsers = Array.from(seen.values());
        } else {
          nextUsers = payloadUsers;
        }
        const invitedUsers =
          payloadRoot?.invitedUsers !== undefined ? payloadRoot.invitedUsers : state.data?.invitedUsers || [];
        const showGoalProgress =
          payloadRoot?.showGoalProgress !== undefined
            ? payloadRoot.showGoalProgress
            : state.data?.showGoalProgress;
        const pagination =
          payloadRoot?.pagination !== undefined ? payloadRoot.pagination : state.data?.pagination;

        state.data = {
          users: nextUsers,
          invitedUsers,
          showGoalProgress,
          pagination
        };
        state.errorText = '';
      })
      .addCase(fetchGroupUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.data = undefined;
        state.errorText = action.error.message || 'Something went wrong';
      });
  }
});

export default groupUsersSlice.reducer;
export const groupUsersActions = groupUsersSlice.actions;
